import type { Painter } from "./colors";

export interface Column {
  header: string;
  align?: "left" | "right";
}

export type Cell = string | { text: string; paint?: Painter };

const MAX_CELL_WIDTH = 44;

/**
 * Minimal dependency-free table renderer. Width math uses the plain text;
 * paint functions are applied after padding so ANSI codes never skew layout.
 */
export function renderTable(cols: Column[], rows: Cell[][], indent = "  "): string {
  const plain = (cell: Cell): string => (typeof cell === "string" ? cell : cell.text);
  const widths = cols.map((col, i) => {
    let w = col.header.length;
    for (const row of rows) {
      const cell = row[i];
      if (cell !== undefined) w = Math.max(w, Math.min(plain(cell).length, MAX_CELL_WIDTH));
    }
    return w;
  });

  const renderRow = (cells: Cell[], painter?: Painter): string => {
    const parts = cols.map((col, i) => {
      const cell = cells[i] ?? "";
      let text = plain(cell);
      if (text.length > MAX_CELL_WIDTH) text = `${text.slice(0, MAX_CELL_WIDTH - 1)}…`;
      const w = widths[i] ?? 0;
      const padded = col.align === "right" ? text.padStart(w) : text.padEnd(w);
      const paint = typeof cell === "object" ? cell.paint : undefined;
      const chosen = paint ?? painter;
      return chosen ? chosen(padded) : padded;
    });
    return indent + parts.join("  ");
  };

  const lines: string[] = [];
  lines.push(renderRow(cols.map((c) => c.header)));
  lines.push(indent + widths.map((w) => "─".repeat(w)).join("──"));
  for (const row of rows) lines.push(renderRow(row));
  return lines.join("\n");
}
